import React, { useState, useEffect } from 'react';
import { usePaymentTimer, formatTime } from '../../hooks/usePaymentTimer';
import { useReownWallet } from '../../hooks/useReownWallet';
import { useAppKit } from '@reown/appkit/react';

const ChainSelector = ({ 
  availableChains, 
  currentChainId,
  paymentData,
  onChainConfirmed,
  onCancel 
}) => {
  const [selectedChain, setSelectedChain] = useState(null);
  const [isSwitching, setIsSwitching] = useState(false);
  const [switchError, setSwitchError] = useState(null);

  const timeRemaining = usePaymentTimer(paymentData?.expiresAt);
  const { chainId: walletChainId } = useReownWallet();
  const { open } = useAppKit();

  // Auto-proceed when chain matches after switch
  useEffect(() => {
    if (selectedChain && walletChainId && Number(walletChainId) === Number(selectedChain.chainId) && !isSwitching) {
      console.log('✅ Chain matched, proceeding...');
      setIsSwitching(false);
      onChainConfirmed(selectedChain, Number(walletChainId));
    }
  }, [walletChainId, selectedChain, isSwitching, onChainConfirmed]);

  const handleChainSelect = async (chain) => {
    console.log('⛓️ Chain selected:', chain);
    setSelectedChain(chain);
    setSwitchError(null);

    const currentChain = walletChainId || currentChainId;

    if (Number(currentChain) === Number(chain.chainId)) {
      console.log('✅ Already on correct chain');
      onChainConfirmed(chain, Number(currentChain));
    } else {
      console.log('🔄 Need to switch to:', chain.name);
      await switchToChain(chain);
    }
  };

  const switchToChain = async (chain) => {
    setIsSwitching(true);
    setSwitchError(null);

    try {
      console.log('🔄 Opening Reown network selector...');
      
      // Use Reown's built-in network switcher
      // This opens the modal with the Networks view
      await open({ view: 'Networks' });
      
      console.log('✅ Network selection modal opened');
      
      // Note: User must manually select the network in the modal
      // The useEffect above will detect when they've switched and proceed automatically
      
    } catch (error) {
      console.error('❌ Network switch error:', error);
      
      let errorMessage;
      
      if (error.code === 4001) {
        errorMessage = 'You cancelled the network switch. Please try again.';
      } else if (error.message) {
        errorMessage = error.message;
      } else {
        errorMessage = 'Failed to open network selector. Please try again.';
      }
      
      setSwitchError(errorMessage);
      setIsSwitching(false);
      setSelectedChain(null);
    }
  };

  return (
    <div className="chain-selector">
      <div className={timeRemaining < 30000 ? 'timer timer-warning' : 'timer'}>
        <span style={{ marginRight: '8px' }}>⏰</span>
        <span>Time Remaining: </span>
        <span className="timer-value">{formatTime(timeRemaining)}</span>
      </div>

      <h2 className="section-title">Select Payment Network</h2>

      <p className="section-description">
        Choose the blockchain network you want to pay from.
      </p>

      {walletChainId && (
        <div className="current-chain-info">
          <span className="current-chain-label">Your wallet is currently on: </span>
          <span className="current-chain-value">
            {availableChains.find(c => Number(c.chainId) === Number(walletChainId))?.name || `Chain ${walletChainId}`}
          </span>
        </div>
      )}

      {switchError && (
        <div className="alert alert-error">{switchError}</div>
      )}

      {isSwitching && (
        <div className="alert" style={{ 
          background: '#fff3cd', 
          color: '#856404', 
          border: '1px solid #ffc107',
          padding: '12px',
          borderRadius: '8px',
          marginBottom: '20px',
          textAlign: 'center'
        }}>
          <strong>🔄 Switching to {selectedChain?.name}...</strong><br/>
          <span style={{ fontSize: '14px' }}>Please select the network in the Reown modal</span>
        </div>
      )}

      <div className="chains-grid">
        {availableChains.map(chain => {
          const isCurrentChain = Number(walletChainId) === Number(chain.chainId);
          const isSelected = selectedChain?.chainId === chain.chainId;
          
          return (
            <button
              key={chain.chainId}
              onClick={() => handleChainSelect(chain)}
              disabled={isSwitching || timeRemaining === 0}
              className={[
                'chain-card',
                isSelected ? 'chain-card-selected' : '',
                isCurrentChain ? 'chain-card-current' : ''
              ].filter(Boolean).join(' ')}
            >
              <div className="chain-card-header">
                <span className="chain-card-name">{chain.name}</span>
                {isCurrentChain && (
                  <span className="chain-card-badge">Current</span>
                )}
              </div>
              
              <div className="chain-card-tokens">
                <span className="tokens-label">Available tokens:</span>
                <div className="tokens-list">
                  {chain.whitelistedTokens.slice(0, 4).map(token => (
                    <span key={token} className="token-badge">{token}</span>
                  ))}
                  {chain.whitelistedTokens.length > 4 && (
                    <span className="token-badge">+{chain.whitelistedTokens.length - 4}</span>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <div className="chain-selector-footer">
        <button 
          onClick={onCancel} 
          className="btn btn-secondary" 
          disabled={isSwitching}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default ChainSelector;